import React, { useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import "./PrintCustomReceipt.styles.scss";
import { TextField, FunctionField, ReferenceField, useShowContext } from "react-admin";
import moment from "moment";
import { getSplitReceipts, priceInWords } from "../../utils";
import KEYS, { getReceiptAPI, getPartyAPI } from "../../api";
import { useQuery } from "react-query";

const PrintCustomReceipt = React.forwardRef((props, ref) => {
    const { record } = useShowContext();

    const accountNumber = record && record.account_number;
    const recordId = record && record.id;

    const { data: paymentsData, refetch: getPaymentsData } = useQuery(
        [KEYS.GET_RECEIPT, accountNumber, recordId],
        () =>
            getReceiptAPI({
                queryParams: {
                    account_number: `eq.${accountNumber}`,
                    order: "account_number.asc.nullslast",
                    id: `lte.${recordId}`,
                    is_deleted: "eq.false",
                },
            }),
        {
            enabled: false,
        }
    );

    const { data: partyData, refetch: getPartyData } = useQuery(
        [KEYS.GET_PARTY, accountNumber],
        () =>
            getPartyAPI({
                queryParams: {
                    order: "account_number.desc.nullslast",
                    limit: 1,
                    account_number_with_ledger_number: `eq.${accountNumber}`,
                },
            }),
        {
            enabled: false,
        }
    );

    useEffect(() => {
        if (accountNumber && recordId) {
            getPaymentsData();
            getPartyData();
        }
    }, [getPaymentsData, getPartyData, accountNumber, recordId]);

    const { dueDates, dueIndexRange } = useMemo(() => {
        if (partyData && partyData.length && paymentsData && paymentsData.length) {
            if (paymentsData && partyData) {
                const party = partyData[0];
                const { due_start: dueStart, number_of_dues: numberOfDues, due_amount: dueAmount, default_interest: defaultInterest } = party;

                const receipts = getSplitReceipts({
                    numberOfDues,
                    dueAmount,
                    defaultInterest,
                    dueStart,
                    payments: paymentsData,
                }).filter(
                    ({ notPaidInPreviousReceiptsAndPending, paidInPreviousReceipts }) =>
                        !notPaidInPreviousReceiptsAndPending && !paidInPreviousReceipts
                );

                const dueIndexRange = receipts
                    .map(({ dueIndex }) => dueIndex + 2)
                    .map((dueIndex) => {
                        const j = dueIndex % 10,
                            k = dueIndex % 100;
                        if (j === 1 && k !== 11) {
                            return dueIndex + "st";
                        }
                        if (j === 2 && k !== 12) {
                            return dueIndex + "nd";
                        }
                        if (j === 3 && k !== 13) {
                            return dueIndex + "rd";
                        }
                        return dueIndex + "th";
                    });

                const dueDates = receipts.map(({ dueDate }) => moment(dueDate).add(1, "month").format("DD/MM/YYYY"));

                return {
                    dueIndexRange: `${dueIndexRange[0]}${
                        dueIndexRange[0] === dueIndexRange[dueIndexRange.length - 1] ? "" : ` to ${dueIndexRange[dueIndexRange.length - 1]}`
                    }`,
                    dueDates: dueDates.join(", "),
                };
            }
        }
        return {};
    }, [paymentsData, partyData]);

    return (
        <div ref={ref} className="print-form-container">
            <Grid container className="print-form-content">
                <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                    <Typography className="heading">
                        A/c No : <TextField className="content" source="account_number" />
                    </Typography>
                    <Grid item xs="auto" container direction="column">
                        <Typography className="heading">
                            L. No : <span className="content">9/2004-05</span>{" "}
                        </Typography>
                        <Typography className="heading phone">
                            Ph No : <span className="content">98429 84920</span>{" "}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} container justifyContent="center" alignItems="center">
                    <Typography variant="h3" className="main-heading">
                        P.G. MONEY LENDERS
                    </Typography>
                </Grid>
                <Grid item xs={12} container justifyContent="center" alignItems="center">
                    <Typography variant="h3" className="address">
                        6,LYOLA COMPLEX, A.A.ROAD,GNANAOLIVUPURAM, MADURAI -625016
                    </Typography>
                </Grid>
                <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                    <Grid item xs={3} container justifyContent="flex-start" alignItem="center">
                        <Typography className="heading">
                            Custom Rcpt No: <TextField className="content underlined" source="id" />
                        </Typography>
                    </Grid>
                    <Grid item xs={6} container justifyContent="center" alignItem="center">
                        <Typography className="main-heading-2 underlined">RECEIPT</Typography>
                    </Grid>
                    <Grid item xs={3} container justifyContent="flex-end" alignItem="center">
                        <Typography className="heading">
                            Date :{" "}
                            <FunctionField className="content underlined" render={(record) => moment(record.created_at).format("DD / MM / YYYY")} />
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} container alignItems="center">
                    <Grid item xs="auto" container alignItems="center">
                        <Typography className="content-heading">Received with thanks from Sri.</Typography>
                    </Grid>
                    <Grid item xs={true} alignItems="center" className="print-form__value">
                        <ReferenceField source="account_number" reference="parties" link={null}>
                            <FunctionField className="form-content" render={(record) => record.name} />
                        </ReferenceField>
                    </Grid>
                </Grid>
                <Grid item xs={12} container alignItems="center">
                    <Grid item xs="auto" container alignItems="center">
                        <Typography className="content-heading">The Sum Of Rupees</Typography>
                    </Grid>
                    <Grid item xs={true} alignItems="center" className="print-form__value">
                        <FunctionField className="form-content" render={(record) => priceInWords(Math.ceil(record.amount)) + " Only"} />
                    </Grid>
                </Grid>
                <Grid item xs={12} container alignItems="center">
                    <Grid item xs="auto" container alignItems="center">
                        <Typography className="content-heading">for Vehicle No. </Typography>
                    </Grid>
                    <Grid item xs={true} alignItems="center" className="print-form__value">
                        <ReferenceField source="account_number" reference="documents">
                            <FunctionField className="form-content" render={(record) => record.registration_number} />
                        </ReferenceField>
                    </Grid>
                    <Grid item xs="auto" container alignItems="center">
                        <Typography className="content-heading"> , for </Typography>
                    </Grid>
                    <Grid item xs={true} alignItems="center" className="print-form__value">
                        <FunctionField
                            className="form-content"
                            render={(record) => {
                                const { type, remarks } = record;
                                if (type === "OTHERS") {
                                    return remarks;
                                }
                                return type;
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                    <FunctionField render={(record) => <Typography className="total-amt">RS. {record.amount} /-</Typography>} />
                    <Typography className="party-name_footer">for P.G. MONEY LENDERS</Typography>
                </Grid>
                <Grid item xs={12} container justifyContent="flex-end" alignItems="center">
                    {/* <Typography className="party-name_footer">Sign</Typography> */}
                </Grid>
                <Grid item xs={12} container justifyContent="flex-end" alignItems="center">
                    <Typography className="party-name_footer">partner / Manager</Typography>
                </Grid>
            </Grid>
        </div>
    );
});

export default PrintCustomReceipt;
